<template>
  <div class="public-root">
    <div class="brand-bar">
      <img src="/assets/images/paysol_logo.png" alt="Paysol" class="brand-logo" />
      <span class="brand-tag">{{ t('publicBooking.brandTag') }}</span>
      <LanguageToggle />
    </div>

    <div v-if="loadingTenant" class="loader">{{ t('common.loading') }}</div>

    <div v-else-if="!tenant" class="error-panel">
      <h2>{{ t('publicBooking.notFoundTitle') }}</h2>
      <p>{{ t('publicBooking.notFoundSub') }}</p>
    </div>

    <div v-else class="wrap">
      <div class="hero card">
        <h1>{{ tenant.name }}</h1>
        <p v-if="tenant.city" class="muted">📍 {{ tenant.city }}</p>
      </div>

      <div v-if="success" class="card success-panel">
        <div class="check"><i class="mdi mdi-check-circle"></i></div>
        <h2>{{ t('publicBooking.bookedTitle') }}</h2>
        <p>{{ success.business }} — {{ success.service }}</p>
        <p class="muted">{{ prettyDate(success.startAt) }}</p>
        <div class="reference">{{ t('publicBooking.reference') }}: <strong>{{ success.reference }}</strong></div>
        <p class="muted small">{{ t('publicBooking.smsOnItsWay') }}</p>
        <button class="secondary" @click="resetForNew">{{ t('publicBooking.bookAnother') }}</button>
      </div>

      <template v-else>
        <div class="card">
          <h3>{{ t('publicBooking.step1') }}</h3>
          <div v-if="services.length" class="service-grid">
            <div v-for="s in services" :key="s.variant_id" class="service-tile"
                 :class="{ picked: booking.serviceVariantId === s.variant_id }"
                 @click="pickService(s)">
              <div class="s-name">{{ s.name }}</div>
              <div class="s-meta">{{ Math.round(s.duration_minutes || 30) }} min</div>
              <div class="s-price">KES {{ format(s.sell_price) }}</div>
            </div>
          </div>
          <p v-else class="muted small">{{ t('publicBooking.noServices') }}</p>
        </div>

        <div class="card" v-if="booking.serviceVariantId">
          <h3>{{ t('publicBooking.step2') }}</h3>
          <div v-if="providers.length" class="prov-list">
            <button v-for="p in providers" :key="p.id"
                    :class="{ 'prov-tile': true, picked: booking.providerUserId === p.id }"
                    @click="pickProvider(p)">
              <div class="prov-name">{{ p.name }}</div>
            </button>
          </div>
          <p v-else class="muted small">{{ t('publicBooking.noProviders') }}</p>
        </div>

        <div class="card" v-if="booking.serviceVariantId && booking.providerUserId">
          <h3>{{ t('publicBooking.step3') }}</h3>
          <div class="date-row">
            <input type="date" v-model="booking.date" :min="today" @change="loadSlots" />
          </div>
          <div v-if="loadingSlots" class="muted small" style="margin-top:8px;">{{ t('publicBooking.loadingSlots') }}</div>
          <div v-else-if="slots.length" class="slots-grid">
            <button v-for="s in slots" :key="s"
                    :class="{ 'slot-tile': true, picked: booking.time === s }"
                    @click="booking.time = s">{{ s }}</button>
          </div>
          <p v-else-if="booking.date" class="muted small" style="margin-top:8px;">{{ t('publicBooking.fullyBooked') }}</p>
        </div>

        <div class="card" v-if="booking.serviceVariantId && booking.providerUserId && booking.time">
          <h3>{{ t('publicBooking.step4') }}</h3>
          <label>{{ t('publicBooking.yourName') }}</label>
          <input v-model="booking.guestName" :placeholder="t('publicBooking.yourNameEg')" />
          <label>{{ t('publicBooking.phoneForSms') }}</label>
          <input v-model="booking.guestPhone" placeholder="0712345678" />
          <label>{{ t('publicBooking.notesOptional') }}</label>
          <input v-model="booking.notes" :placeholder="t('publicBooking.notesPlaceholder')" />
          <button class="primary full" @click="confirmBook" :disabled="booking.booking || !canBook">
            <i class="mdi mdi-calendar-check"></i>
            {{ booking.booking ? t('publicBooking.booking') : t('publicBooking.confirm') }}
          </button>
          <p v-if="err" class="err">{{ err }}</p>
        </div>
      </template>

      <p class="footer">Powered by <a href="https://www.paysol.africa" target="_blank">Paysol</a></p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { paysol } from '@/api';
import { t } from '@/i18n';
import LanguageToggle from '@/i18n/LanguageToggle.vue';

export default {
  name: 'PublicBookingPage',
  components: { LanguageToggle },
  data() {
    return {
      localeTick: 0,
      slug: this.$route.params.slug,
      loadingTenant: true, tenant: null,
      services: [], providers: [], slots: [],
      loadingSlots: false,
      today: new Date().toISOString().slice(0, 10),
      booking: {
        serviceVariantId: 0, providerUserId: 0,
        date: '', time: '',
        guestName: '', guestPhone: '', notes: '',
        booking: false,
      },
      success: null, err: '',
    };
  },
  computed: {
    canBook() { return this.booking.guestName && this.booking.guestPhone && this.booking.time; }
  },
  async mounted() {
    this._onLoc = () => { this.localeTick++; };
    window.addEventListener('paysol-locale-changed', this._onLoc);
    await this.load();
  },
  beforeUnmount() {
    if (this._onLoc) window.removeEventListener('paysol-locale-changed', this._onLoc);
  },
  methods: {
    t(key, vars) { this.localeTick; return t(key, vars); },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); },
    prettyDate(iso) {
      const d = new Date(iso.replace(' ', 'T'));
      return d.toLocaleString('en-KE', { weekday: 'long', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    async post(fn, params) {
      return await axios.post(paysol, { function: fn, slug: this.slug, ...params });
    },
    async load() {
      this.loadingTenant = true;
      try {
        const { data } = await this.post('publicGetTenant', {});
        if (!data.success) return;
        this.tenant = data.data;
        const [svc, prov] = await Promise.all([
          this.post('publicListServices', {}),
          this.post('publicListProviders', {}),
        ]);
        if (svc.data.success) this.services = svc.data.data;
        if (prov.data.success) this.providers = prov.data.data;
        this.booking.date = this.today;
      } catch (e) { /* silent */ }
      finally { this.loadingTenant = false; }
    },
    pickService(s) {
      this.booking.serviceVariantId = s.variant_id;
      this.booking.time = '';
      this.slots = [];
      if (this.booking.providerUserId) this.loadSlots();
    },
    pickProvider(p) {
      this.booking.providerUserId = p.id;
      this.booking.time = '';
      this.slots = [];
      this.loadSlots();
    },
    async loadSlots() {
      if (!this.booking.serviceVariantId || !this.booking.providerUserId || !this.booking.date) return;
      this.loadingSlots = true;
      try {
        const { data } = await this.post('publicListSlots', {
          providerId: this.booking.providerUserId,
          serviceVariantId: this.booking.serviceVariantId,
          date: this.booking.date,
        });
        this.slots = data.success ? data.data.slots : [];
      } finally { this.loadingSlots = false; }
    },
    async confirmBook() {
      this.err = ''; this.booking.booking = true;
      try {
        const { data } = await this.post('publicBookAppointment', {
          serviceVariantId: this.booking.serviceVariantId,
          providerUserId: this.booking.providerUserId,
          date: this.booking.date,
          time: this.booking.time,
          guestName: this.booking.guestName,
          guestPhone: this.booking.guestPhone,
          notes: this.booking.notes,
        });
        if (!data.success) { this.err = data.message; return; }
        this.success = data.data;
      } finally { this.booking.booking = false; }
    },
    resetForNew() {
      this.success = null;
      this.booking = { serviceVariantId: 0, providerUserId: 0, date: this.today, time: '', guestName: '', guestPhone: '', notes: '', booking: false };
      this.slots = []; this.err = '';
    }
  }
};
</script>

<style scoped>
.public-root { min-height: 100vh; background: var(--paysol-bg); color: var(--paysol-text); font-family: var(--paysol-font); }
.brand-bar { background: var(--paysol-bg-elev); padding: 12px 20px; border-bottom: 1px solid var(--paysol-line); display: flex; align-items: center; justify-content: space-between; }
.brand-logo { height: 32px; filter: brightness(1.05); }
.brand-tag { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
.wrap { max-width: 560px; margin: 0 auto; padding: 24px 16px 40px; display: flex; flex-direction: column; gap: 12px; }
.hero { padding: 24px; text-align: center; }
.hero h1 { margin: 0 0 6px; font-size: 28px; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.loader { text-align: center; padding: 100px 20px; color: var(--paysol-muted); }
.error-panel { text-align: center; padding: 60px 20px; max-width: 480px; margin: 0 auto; }

.card { padding: 20px; }
h3 { color: var(--paysol-teal); font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 14px; }

.service-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; }
.service-tile {
  background: var(--paysol-bg-elev); border: 2px solid var(--paysol-line);
  border-radius: 8px; padding: 14px; cursor: pointer; transition: border-color .15s, transform .1s;
}
.service-tile:hover { border-color: var(--paysol-teal); }
.service-tile.picked { border-color: var(--paysol-teal); background: var(--paysol-teal-glow); }
.s-name { color: var(--paysol-text); font-weight: 600; margin-bottom: 4px; }
.s-meta { color: var(--paysol-muted); font-size: 11px; }
.s-price { color: var(--paysol-teal); font-weight: 700; margin-top: 6px; }

.prov-list { display: flex; flex-wrap: wrap; gap: 8px; }
.prov-tile {
  background: var(--paysol-bg-elev); border: 2px solid var(--paysol-line); color: var(--paysol-text);
  padding: 10px 16px; border-radius: 8px; cursor: pointer; font-weight: 500; transition: border-color .15s;
}
.prov-tile:hover { border-color: var(--paysol-teal); }
.prov-tile.picked { border-color: var(--paysol-teal); background: var(--paysol-teal-glow); color: var(--paysol-teal); }
.prov-name { }

.date-row input { max-width: 220px; }
.slots-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 6px; margin-top: 12px; }
.slot-tile {
  background: var(--paysol-bg-elev); border: 1px solid var(--paysol-line); color: var(--paysol-text);
  padding: 10px 8px; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 13px;
}
.slot-tile:hover { border-color: var(--paysol-teal); }
.slot-tile.picked { background: var(--paysol-teal); color: #fff; border-color: var(--paysol-teal); }

button.full { width: 100%; padding: 14px; font-size: 15px; margin-top: 16px; display: inline-flex; align-items: center; justify-content: center; gap: 6px; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; text-align: center; }

.success-panel { text-align: center; padding: 40px 24px; }
.check i { font-size: 60px; color: var(--paysol-success); }
.success-panel h2 { margin: 12px 0 4px; }
.reference { background: var(--paysol-teal-glow); color: var(--paysol-teal); padding: 10px 16px; border-radius: 6px; margin: 20px 0 10px; display: inline-block; }
.reference strong { letter-spacing: 1px; font-family: 'JetBrains Mono', monospace; }

.footer { text-align: center; color: var(--paysol-muted); font-size: 11px; margin-top: 20px; }
.footer a { color: var(--paysol-teal); text-decoration: none; }
</style>
